import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, Trash2, Check, X, Star } from "lucide-react";

const BADGE = { pending: "bg-gold-light text-gold-dark", approved: "bg-sage-light text-sage-dark", rejected: "bg-coral-light text-coral-dark" };
const LABEL = { pending: "Awaiting approval", approved: "Visible to families", rejected: "Not approved" };

export const galleryUrl = (staffId, photoId) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/gallery/${staffId}/${photoId}?auth=${localStorage.getItem("bsb_token")}`;

const thisMonday = () => {
  const d = new Date(); const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
};

export function ActivityGallery({ staffId, manage = false }) {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const base = manage ? `/staff/${staffId}/gallery` : "/me/gallery";
  const load = () => api.get(base).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [staffId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("caption", caption);
    setBusy(true);
    try { await api.post("/me/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo sent to the office for approval"); setCaption(""); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm("Remove this photo?")) return;
    try { await api.delete(`${base}/${p.id}`); toast.success("Photo removed"); load(); } catch (err) { toast.error(apiError(err)); }
  };
  const review = async (p, status) => {
    try { await api.put(`${base}/${p.id}/status`, { status }); toast.success(status === "approved" ? "Approved — families can now see it" : "Photo rejected"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const feature = async (p) => {
    try { const r = await api.put(`${base}/${p.id}/feature`); toast.success(r.data.featured_week ? "Set as photo of the week" : "Photo of the week cleared"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const live = items.filter((p) => p.status !== "rejected").length;

  return (
    <div data-testid={manage ? "manage-gallery" : "my-gallery"}>
      {items.length === 0 && <p className="mb-3 text-sm text-ink-muted" data-testid="gallery-empty">{manage ? "No activity photos yet." : "No activity photos yet — add up to 3."}</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {items.map((p) => (
          <figure key={p.id} data-testid={`gallery-item-${p.id}`} className="overflow-hidden rounded-xl border border-border bg-white">
            <img src={galleryUrl(staffId, p.id)} alt={p.caption || "Activity"} className="h-36 w-full object-cover" />
            <figcaption className="space-y-2 p-3">
              {p.caption && <p className="text-sm text-ink">{p.caption}</p>}
              <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${BADGE[p.status]}`} data-testid={`gallery-status-${p.id}`}>{LABEL[p.status]}</span>
              {p.featured_week && p.featured_week === thisMonday() && <span data-testid={`gallery-featured-${p.id}`} className="ml-1 inline-flex items-center gap-1 rounded-full bg-gold-light px-2 py-0.5 text-xs font-semibold text-gold-dark"><Star size={11} /> Photo of the week</span>}
              <div className="flex flex-wrap gap-2">
                {manage && p.status !== "approved" && <button onClick={() => review(p, "approved")} data-testid={`gallery-approve-${p.id}`} className="inline-flex items-center gap-1 rounded-lg bg-sage px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90"><Check size={13} /> Approve</button>}
                {manage && p.status === "pending" && <button onClick={() => review(p, "rejected")} data-testid={`gallery-reject-${p.id}`} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-semibold text-ink hover:bg-secondary"><X size={13} /> Reject</button>}
                {manage && p.status === "approved" && <button onClick={() => feature(p)} data-testid={`gallery-feature-${p.id}`} className="inline-flex items-center gap-1 rounded-lg border border-gold/40 px-2.5 py-1 text-xs font-semibold text-gold-dark hover:bg-gold-light"><Star size={13} /> {p.featured_week === thisMonday() ? "Unfeature" : "Photo of the week"}</button>}
                <button onClick={() => remove(p)} data-testid={`gallery-remove-${p.id}`} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-semibold text-coral-dark hover:bg-coral-light"><Trash2 size={13} /> Remove</button>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
      {!manage && live < 3 && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={140} placeholder="Short caption, e.g. Walking my dog by the river"
            data-testid="gallery-caption-input" className="flex-1 rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
          <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark">
            <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} data-testid="gallery-file-input" />
            <ImagePlus size={16} /> {busy ? "Uploading…" : "Add photo"}
          </label>
        </div>
      )}
    </div>
  );
}
